/* eslint-disable check-file/filename-blocklist */
import { defineMessages } from 'react-intl';

export const commonI18n: {
	dismiss: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	showMeHow: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	spotlightCta: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	spotlightDismiss: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	dismiss: {
		id: 'automation-discovery.common.dismiss',
		defaultMessage: 'Not now',
		description:
			'The text is shown on a button in the automation discovery prompt that allows the user to dismiss it without taking action, deferring it for later.',
	},
	showMeHow: {
		id: 'automation-discovery.common.show-me-how',
		defaultMessage: 'Show me how',
		description: 'Label to prompt the user to click into the in-context rule builder',
	},
	spotlightCta: {
		id: 'automation-discovery.manual-trigger.cta.text',
		defaultMessage: 'Build rule',
		description: 'Label taking the user to set up an automation rule',
	},
	spotlightDismiss: {
		id: 'automation-discovery.manual-trigger.dismiss',
		defaultMessage: 'Done',
		description:
			'The text is shown on a button in the manual trigger automation spotlight that allows the user to dismiss the spotlight after reviewing it.',
	},
});

export const hoverPageTitleI18n: {
	header: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	message: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	header: {
		id: 'automation-discovery.hover-page-title.header',
		defaultMessage: 'Auto-archive old pages?',
		description: 'A header asking the user to automatically archiving old pages',
	},
	message: {
		id: 'automation-discovery.hover-page-title.message',
		defaultMessage:
			'This page hasn’t been updated lately. An Automation rule could archive old pages once a month.',
		description: 'Message to user suggesting how to automatically archive old pages',
	},
});

export const archiveMessagesI18n: {
	header: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	message: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	success: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	successHeader: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	viewInArchive: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	header: {
		id: 'automation-discovery.archive.from-archive-modal',
		defaultMessage: 'Confluence can auto-archive old pages',
		description: 'A header telling the user about automatically archiving old pages',
	},
	message: {
		id: 'automation-discovery.archive.message',
		defaultMessage:
			'Try building this Automation rule: Archive pages in this space that haven’t been updated in at least 3 months.',
		description: 'Message to use to build an automation rule that archives old pages in a space',
	},
	success: {
		id: 'automation-discovery.archive.success',
		defaultMessage: 'Your page was archived',
		description: 'Message to user confirming the page was archived successfully',
	},
	successHeader: {
		id: 'automation-discovery.archive.success-header',
		defaultMessage: 'Archived successfully',
		description: 'A header message to user confirming the page was archived successfully',
	},
	viewInArchive: {
		id: 'automation-discovery.archive.view-in-archive',
		defaultMessage: 'View in archive',
		description: 'A link to the archive page, which has a list of all archived items',
	},
});

export const labelMessagesI18n: {
	title: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	message: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	title: {
		id: 'automation-discovery.add-label.title',
		defaultMessage: 'Confluence can auto-add labels',
		description: 'Message to user about automating adding labels',
	},
	message: {
		id: 'automation-discovery.add-label.message',
		defaultMessage:
			'Try building this Automation rule: When the user you choose publishes a new page, label it with their name.',
		description: 'Message to user to try building automation rule that add labels',
	},
});

export const contentReviewerNudgeI18n: {
	title: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	secondaryTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	iconLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readyForReviewState: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	title: {
		id: 'automation-discovery.content-reviewer-nudge-action.title',
		defaultMessage: 'Get proactive content suggestions',
		description:
			'Heading in the Rovo button nudge shown when a page is ready to be reviewed, inviting the user to have Rovo review the page.',
	},
	secondaryTitle: {
		id: 'automation-discovery.content-reviewer-nudge-action.secondary-title',
		defaultMessage: 'Automate the next content review.',
		description:
			'Secondary heading in the Rovo button nudge shown when a page is ready to be reviewed, inviting the user to have Rovo review the page.',
	},
	iconLabel: {
		id: 'automation-discovery.content-reviewer-nudge-action.icon-label',
		defaultMessage: 'Automation icon',
		description: 'Accessible label for the icon in the content-reviewer Rovo button nudge action.',
	},
	readyForReviewState: {
		id: 'automation-discovery.content-state.default.status.ready.for.review',
		defaultMessage: 'Ready for review',
		description:
			'One of three default statuses shown if space admin has not customized page status',
	},
});

export const meetingNotesNudgeI18n: {
	title: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	secondaryTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	iconLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	title: {
		id: 'automation-discovery.meeting-notes-nudge-action.title',
		defaultMessage: 'Auto-receive an email with your meeting recap',
		description:
			'Heading in the Rovo button nudge shown after publishing meeting notes, inviting the user to automate a recap email for future meeting notes.',
	},
	secondaryTitle: {
		id: 'automation-discovery.meeting-notes-nudge-action.secondary-title',
		defaultMessage: 'Get a summary of your meeting notes straight to your inbox.',
		description:
			'Secondary heading in the Rovo button nudge shown after publishing meeting notes, explaining the automation emails a summary of each published meeting notes page.',
	},
	iconLabel: {
		id: 'automation-discovery.meeting-notes-nudge-action.icon-label',
		defaultMessage: 'Automation icon',
		description: 'Accessible label for the icon in the meeting notes Rovo button nudge action.',
	},
});

export const meetingNotesI18n: {
	success: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	title: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	message: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	ctaText: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	modalBody: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	successIconLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	modalCloseIconLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	success: {
		id: 'automation-discovery.meeting-notes.success',
		defaultMessage: 'Your page was published',
		description: 'Message to user about the page was published successfully',
	},
	title: {
		id: 'automation-discovery.meeting-notes.title',
		defaultMessage: 'Confluence can auto-publish pages',
		description: 'Message to user about Automation can auto-publish pages',
	},
	message: {
		id: 'automation-discovery.meeting-notes.message',
		defaultMessage:
			'Try building this Automation rule: Publish a new Meeting notes page according to your meeting schedule.',
		description: 'Message to user to try building automation rule that auto publish meeting notes',
	},
	ctaText: {
		id: 'automation-discovery.meeting-notes.cta.text',
		defaultMessage: 'Auto-summarize pages?',
		description: 'Asking the user if they want to try to automate summarizing pages in their space',
	},
	modalBody: {
		id: 'automation-discovery.meeting-notes.modal.body',
		defaultMessage: '{publishedPageTitle} was successfully published in the {spaceName} space',
		description: 'Message informing the user they successfully published a page in their space',
	},
	successIconLabel: {
		id: 'automation-discovery.meeting-notes.success.icon.label',
		defaultMessage: 'Success check icon',
		description: 'accessibility label to describe a green success check icon',
	},
	modalCloseIconLabel: {
		id: 'automation-discovery.meeting-notes.modal.close.icon.label',
		defaultMessage: 'Close icon for modal',
		description: 'accessibility label to describe a cross icon to close the modal',
	},
});

export const jiraLinkI18n: {
	ctaText: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	ctaText: {
		id: 'automation-discovery.jira-link.cta.text',
		defaultMessage: 'Auto-link Jira issues?',
		description: 'Asking the user if they want to try to automate linking Jira issues',
	},
});

export const manualTriggerI18n: {
	title: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	body: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	title: {
		id: 'automation-discovery.manual-trigger.title',
		defaultMessage: 'Page automation menu',
		description:
			'A header telling the user about the page automation menu in the Confluence editor',
	},
	body: {
		id: 'automation-discovery.manual-trigger.body',
		defaultMessage: 'Teammates can apply rules from this menu to a single page.',
		description: 'A message telling the user about the page automation menu functionality',
	},
});

export const verifiedContentI18n: {
	title: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	body: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	title: {
		id: 'automation-discovery.verified-content.title',
		defaultMessage: 'Want to manage your verified pages?',
		description: 'A header asking the user if they would like automation to manage verified pages.',
	},
	body: {
		id: 'automation-discovery.verified-content.body',
		defaultMessage: 'Use Confluence automation to schedule reviews for verified content.',
		description: 'A message telling the user about an automation rule to manage verified content.',
	},
});

export const productivityInsightsI18n: {
	productivityTimeSavedMessage: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	productivityTimeSavedMessage: {
		id: 'automation-discovery.productivity-insights.time-saved.message',
		defaultMessage:
			'Save your team <b>{timeSaved, plural, one {{timeSaved} minute} other {{timeSaved} minutes}}</b> per quarter by setting up this automation rule!',
		description:
			'A prompt telling the user how many minutes of work could potentially be saved per quarter by enabling an automation. The timeSaved variable will be a number.',
	},
});
